import { useState, useEffect, useCallback, useRef } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { getAuthorizationToken } from '../utils/jwt';
import apiConfig from '../services/apiConfig';

/**
 * SignalR Manager Hook
 * Provides a reusable way to handle SignalR connections, topics, and event handlers
 */
export const useSignalRManager = () => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [topicSubscriptions, setTopicSubscriptions] = useState(new Map());
  const eventHandlersRef = useRef(new Map());
  const connectionRef = useRef(null);
  const topicSubscriptionsRef = useRef(new Map());
  const jwtToken = getAuthorizationToken(true);

  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  useEffect(() => {
    topicSubscriptionsRef.current = topicSubscriptions;
  }, [topicSubscriptions]);

  const resubscribeToTopics = useCallback(async (conn) => {
    const activeConnection = conn || connectionRef.current;
    if (!activeConnection || activeConnection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    const subscriptionEntries = Array.from(topicSubscriptionsRef.current.entries());

    await subscriptionEntries.reduce(async (promise, [topicConfig, handlers]) => {
      await promise;
      try {
        if (topicConfig.serverMethod && topicConfig.serverMethodParams) {
          await activeConnection.invoke(topicConfig.serverMethod, topicConfig.serverMethodParams);
        }

        const handlerEntries = Array.from(handlers.entries());
        handlerEntries.forEach(([eventName, handler]) => {
          activeConnection.off(eventName, handler);
          activeConnection.on(eventName, handler);
          console.log(`SignalRManager: Re-subscribed to event ${eventName} for topic ${topicConfig.name}`);
        });
      } catch (err) {
        console.error(`SignalRManager: Failed to re-subscribe to topic ${topicConfig.name}:`, err);
      }
    }, Promise.resolve());
  }, []);

  const createConnection = useCallback(async () => {
    if (!jwtToken) {
      console.warn('SignalRManager: No JWT token, connection not established.');
      return null;
    }

    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current;
    }

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${apiConfig.SInspectSignalRAPIUrl}/object-change`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => jwtToken
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => 5000
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    newConnection.onclose((err) => {
      console.error('SignalRManager: Connection closed with error:', err);
      setConnection(null);
      setIsConnected(false);
    });

    newConnection.onreconnecting((err) => {
      console.warn('SignalRManager: Reconnecting...', err);
      setIsConnected(false);
    });

    newConnection.onreconnected(() => {
      setIsConnected(true);
      resubscribeToTopics(newConnection);
    });

    try {
      await newConnection.start();
      setConnection(newConnection);
      setIsConnected(true);
      return newConnection;
    } catch (err) {
      console.error('SignalRManager: Connection Error:', err);
      setConnection(null);
      setIsConnected(false);
      throw err;
    }
  }, [jwtToken, resubscribeToTopics]);

  const subscribeTopic = useCallback(async (topicConfig) => {
    const {
      name,
      eventHandlers = [],
      serverMethod = null,
      serverMethodParams = null,
      onConnect = null
    } = topicConfig;

    try {
      const activeConnection = await createConnection();
      if (!activeConnection) {
        throw new Error('Failed to establish connection');
      }

      if (serverMethod && serverMethodParams) {
        await activeConnection.invoke(serverMethod, serverMethodParams);
      }

      const eventHandlerMap = new Map();
      const allEventHandlers = eventHandlersRef.current.get(name) || new Map();
      eventHandlers.forEach((eventConfig) => {
        const { eventName, handler } = eventConfig;
        const wrappedHandler = (...args) => {
          try {
            handler(...args);
          } catch (err) {
            console.error(`SignalRManager: Error handling ${eventName} event:`, err);
          }
        };
        activeConnection.on(eventName, wrappedHandler);
        eventHandlerMap.set(eventName, wrappedHandler);
        allEventHandlers.set(eventName, wrappedHandler);
      });
      // console.log(`SignalRManager: Subscribed to topic ${name} with events:`, Array.from(eventHandlerMap.keys()));
      eventHandlersRef.current.set(name, allEventHandlers);
      setTopicSubscriptions((prev) => new Map(prev.set(topicConfig, eventHandlerMap)));

      if (onConnect) {
        onConnect(activeConnection);
      }

      return activeConnection;
    } catch (err) {
      console.error(`SignalRManager: Failed to subscribe to topic ${name}:`, err);
      throw err;
    }
  }, [createConnection]);

  const unsubscribeTopic = useCallback(async (topicName, serverUnsubscribeMethod = null, serverUnsubscribeParams = null) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      console.warn(`SignalRManager: Cannot unsubscribe from ${topicName}, no active connection`);
      return;
    }

    try {
      let topicConfigToRemove = null;
      let eventHandlersToRemove = null;

      const subscriptionEntries = Array.from(topicSubscriptionsRef.current.entries());
      const targetSubscription = subscriptionEntries.find(([topicConfig]) => topicConfig.name === topicName);

      if (targetSubscription) {
        [topicConfigToRemove, eventHandlersToRemove] = targetSubscription;

        const handlerEntries = Array.from(eventHandlersToRemove.entries());

        handlerEntries.forEach(([eventName, handler]) => {
          connectionRef.current.off(eventName, handler);
        });

        if (serverUnsubscribeMethod && serverUnsubscribeParams) {
          await connectionRef.current.invoke(serverUnsubscribeMethod, serverUnsubscribeParams);
        }

        setTopicSubscriptions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(topicConfigToRemove);
          return newMap;
        });

        eventHandlersRef.current.delete(topicName);

        if (topicConfigToRemove.onDisconnect) {
          topicConfigToRemove.onDisconnect(connectionRef.current);
        }
      }
    } catch (err) {
      console.error(`SignalRManager: Failed to unsubscribe from topic ${topicName}:`, err);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (connectionRef.current && connectionRef.current.state !== signalR.HubConnectionState.Disconnected) {
      try {
        const subscriptionEntries = Array.from(topicSubscriptionsRef.current.entries());
        await subscriptionEntries.reduce(async (promise, [topicConfig]) => {
          await promise;
          return unsubscribeTopic(topicConfig.name);
        }, Promise.resolve());

        await connectionRef.current.stop();
      } catch (err) {
        console.error('SignalRManager: Error closing connection:', err);
      }
    }
    setConnection(null);
    setIsConnected(false);
    setTopicSubscriptions(new Map());
    eventHandlersRef.current.clear();
  }, [unsubscribeTopic]);

  useEffect(() => {
    let isMounted = true;

    if (jwtToken) {
      createConnection().catch((err) => {
        if (isMounted) {
          console.error('SignalRManager: Failed to create initial connection:', err);
        }
      });
    }

    return () => {
      isMounted = false;
      disconnect();
    };
  }, [jwtToken, createConnection, disconnect]);

  return {
    connection,
    isConnected,
    subscribeTopic,
    unsubscribeTopic,
    disconnect,
    topicSubscriptions: Array.from(topicSubscriptions.keys()).map((config) => config.name)
  };
};

export default useSignalRManager;
